import { CUSTOM_PRINTOUT_EFFORTS, type CustomPrintoutEffort, type CustomPrintoutTurn } from "./customPrintoutTypes";

/** An answered summary question. One still open at a reload is kept as `dismissed`: its request can't be sent again. */
type StoredSummaryQuestionAnswer = "accepted" | "declined" | "dismissed";
const STORED_SUMMARY_QUESTION_ANSWERS: StoredSummaryQuestionAnswer[] = ["accepted", "declined", "dismissed"];

/** A chat entry kept across a reload: a request's turn, or Annie's summary question. Failed requests aren't kept. */
export type StoredChatEntry =
	| { kind: "turn"; turn: CustomPrintoutTurn }
	| { kind: "question"; request: string; hasAttachment: boolean; summary: string; answer: StoredSummaryQuestionAnswer };

/**
 * The most recent unsaved design in this department, kept in this browser so a reload doesn't lose it. The design
 * itself stays on the server (its generation); this only remembers which one it was and the chat that led to it.
 * One per department: the popup shows one design at a time, and leaving one asks to lose it first.
 */
export type StoredCustomPrintoutDraft = {
	/** The saved printout the design is for, or `null` for a new printout. */
	printoutId: string | null;
	/** A new printout's starting point: the saved printout it copies, or `null` for the reference sheet. */
	startFromId: string | null;
	/** The saved printout's version the design was made on. A newer one saved since makes the design out of date. */
	baseVersionId: string | null;
	generationId: string;
	effort: CustomPrintoutEffort;
	/** This session's chat, oldest first: what follows the saved printout's own conversation. */
	entries: StoredChatEntry[];
};

const storageKey = (departmentId: string): string => `custom-printout-draft:${departmentId}`;

const isString = (value: unknown): value is string => typeof value === "string";
const isNullableString = (value: unknown): value is string | null => value === null || isString(value);

const isTurn = (value: unknown): value is CustomPrintoutTurn => {
	if (typeof value !== "object" || value === null) return false;
	const turn = value as Record<string, unknown>;
	return isString(turn.request) && isString(turn.summary) && typeof turn.isRepair === "boolean" && typeof turn.hasAttachment === "boolean";
};

const isEntry = (value: unknown): value is StoredChatEntry => {
	if (typeof value !== "object" || value === null) return false;
	const entry = value as Record<string, unknown>;
	if (entry.kind === "turn") return isTurn(entry.turn);
	return (
		entry.kind === "question" &&
		isString(entry.request) &&
		typeof entry.hasAttachment === "boolean" &&
		isString(entry.summary) &&
		STORED_SUMMARY_QUESTION_ANSWERS.some((answer) => answer === entry.answer)
	);
};

/** Storage is written by this page only, but an older build's record (or a hand edit) must read as no draft, not a crash. */
const isStoredDraft = (value: unknown): value is StoredCustomPrintoutDraft => {
	if (typeof value !== "object" || value === null) return false;
	const record = value as Record<string, unknown>;
	return (
		isNullableString(record.printoutId) &&
		isNullableString(record.startFromId) &&
		isNullableString(record.baseVersionId) &&
		isString(record.generationId) &&
		CUSTOM_PRINTOUT_EFFORTS.some((effort) => effort === record.effort) &&
		Array.isArray(record.entries) &&
		record.entries.every(isEntry)
	);
};

// The catches below log and go on, as `LocalStorageService` does: a private window, a full quota or blocked site
// data make the browser throw, and the design then simply isn't kept across a reload — the same as before this existed.

export const readStoredDraft = (departmentId: string): StoredCustomPrintoutDraft | null => {
	try {
		const raw = window.localStorage.getItem(storageKey(departmentId));
		if (raw === null) return null;
		const parsed: unknown = JSON.parse(raw);
		return isStoredDraft(parsed) ? parsed : null;
	} catch (error) {
		console.error(`Failed to read the custom printout draft of ${departmentId} from localStorage:`, error);
		return null;
	}
};

export const writeStoredDraft = (departmentId: string, draft: StoredCustomPrintoutDraft): void => {
	try {
		window.localStorage.setItem(storageKey(departmentId), JSON.stringify(draft));
	} catch (error) {
		console.error(`Failed to keep the custom printout draft of ${departmentId} in localStorage:`, error);
	}
};

export const clearStoredDraft = (departmentId: string): void => {
	try {
		window.localStorage.removeItem(storageKey(departmentId));
	} catch (error) {
		console.error(`Failed to clear the custom printout draft of ${departmentId} from localStorage:`, error);
	}
};
